import React from 'react'
import { isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import CloseOutlinedIcon from 'components/material-symbol/icons/Close'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import PeopleOutlinedIcon from 'components/material-symbol/icons/People'
import EventNoteOutlinedIcon from 'components/material-symbol/icons/EventNote'
import EventOutlinedIcon from 'components/material-symbol/icons/Event'
import CancelOutlinedIcon from 'components/material-symbol/icons/Cancel'
import CancelBookingDialog from 'components/cancel-booking-dialog'
import AddToCalendarButton from 'components/add-to-calendar-button'
import InformationActionBanner from 'components/banners/banner-info'
import EmojiObjectsOutlinedIcon from 'components/material-symbol/icons/EmojiObjects'
import IconBadge from 'pages/pga-coach/bookings/booking-types/booking-type-details-dialog/icon-badge'
import { useIsMobile, useIsTablet } from 'lib/hooks'
import {
  toLuxonFromIso,
  getTimePeriodWithTimezone,
  toFullTextDateWithTimezone,
} from 'utils/dateUtils'
import { useStyles } from './styles'

const makeCalendarEvent = ({
  id,
  startDateTime,
  endDateTime,
  title,
  lessonCaption,
  coachFacility = {},
}) => ({
  uid: `coach-tools.lesson.${id}.pga.com`,
  startDateTime: toLuxonFromIso(startDateTime),
  endDateTime: toLuxonFromIso(endDateTime),
  title,
  description: lessonCaption,
  location: coachFacility.name,
  url: `${window.location.origin}/club`,
})

const SessionDetailContent = ({
  openLesson,
  setOpenLesson,
  refetchLessons,
}) => {
  const {
    title,
    startDateTime,
    cancellationCode,
    endDateTime,
    timezone,
    enrollments = [],
    coachFacility = {},
    canStudentCancel,
    coach,
    timeShort,
    dateBadge,
    viewingStudentId,
  } = openLesson

  const classes = useStyles()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const isUpcoming =
    !!endDateTime && DateTime.now() < toLuxonFromIso(endDateTime)

  const facilityAddress = [
    coachFacility?.city && `${coachFacility.city},`,
    coachFacility?.state,
    coachFacility?.zipCode,
  ]
    .filter(Boolean)
    .join(' ')

  const activeEnrollments = enrollments.filter(
    ({ isCanceled, student }) => !isCanceled && student,
  )
  const displayedEnrollments = viewingStudentId
    ? activeEnrollments.filter(
      enrollment => String(enrollment.student?.id) === String(viewingStudentId),
    )
    : activeEnrollments
  const showAttendeeCount = !viewingStudentId

  const heroMetaValue =
    dateBadge?.month && dateBadge?.day
      ? `${dateBadge.month} ${dateBadge.day}`
      : timeShort

  return (
    <>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.hero}>
          <Box className={classes.actionButtonContainer}>
            <Avatar
              className={classes.actionAvatar}
              aria-label="Close"
              onClick={() => setOpenLesson({})}
            >
              <CloseOutlinedIcon color="primary" />
            </Avatar>
          </Box>
          <Box className={classes.heroContent}>
            {isUpcoming ? (
              <Chip
                label="Upcoming"
                size="small"
                className={classes.statusChip}
              />
            ) : null}
            <Typography className={classes.heroTitle}>{title}</Typography>
            {coach?.name ? (
              <Typography className={classes.heroSubtitle}>
                Coach {coach.name}
              </Typography>
            ) : null}
          </Box>
          {heroMetaValue ? (
            <Box className={classes.heroMeta}>
              <Typography className={classes.heroMetaLabel}>When</Typography>
              <Typography className={classes.heroMetaValue}>
                {heroMetaValue}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </DialogTitle>
      <DialogContent className={classes.contentRoot}>
        <Box className={classes.detailsCard}>
          <Typography className={classes.sectionLabel}>
            Session details
          </Typography>

          {coachFacility?.name ? (
            <Box className={classes.detailRow}>
              <IconBadge icon={RoomOutlinedIcon} />
              <Box minWidth={0}>
                <Typography className={classes.detailPrimary}>
                  {coachFacility.name}
                </Typography>
                {facilityAddress ? (
                  <Typography className={classes.detailSecondary}>
                    {facilityAddress}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : null}

          <Box className={classes.detailRow}>
            <IconBadge icon={EventNoteOutlinedIcon} />
            <Box className={classes.detailRowContent}>
              <Box className={classes.detailCopy}>
                <Typography className={classes.detailPrimary}>
                  {toFullTextDateWithTimezone(startDateTime, timezone)}
                </Typography>
                <Typography className={classes.detailSecondary}>
                  {getTimePeriodWithTimezone(
                    startDateTime,
                    endDateTime,
                    timezone,
                  )}
                </Typography>
              </Box>
              {isUpcoming ? (
                <AddToCalendarButton
                  event={makeCalendarEvent(openLesson)}
                  variant="contained"
                  className={classes.calendarButton}
                  startIcon={<EventOutlinedIcon fontSize="small" />}
                >
                  Add to calendar
                </AddToCalendarButton>
              ) : null}
            </Box>
          </Box>

          {showAttendeeCount ? (
            <Box className={classes.detailRow}>
              <IconBadge icon={PeopleOutlinedIcon} />
              <Typography className={classes.detailPrimary}>
                {displayedEnrollments.length} Attendee
                {displayedEnrollments.length === 1 ? '' : 's'}
              </Typography>
            </Box>
          ) : null}

          {displayedEnrollments.map(enrollment => {
            const { firstName, lastName } = enrollment.student
            const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(
              0,
            ) || ''}`

            return (
              <Box key={enrollment.id} className={classes.detailRow}>
                <Avatar className={classes.attendeeAvatar}>{initials}</Avatar>
                <Typography className={classes.detailPrimary}>
                  {firstName} {lastName}
                </Typography>
              </Box>
            )
          })}
        </Box>

        {isUpcoming ? (
          <Box className={classes.footerActions}>
            {canStudentCancel ? (
              <Box className={classes.cancelCard}>
                <Box className={classes.cancelCopy}>
                  <Typography className={classes.cancelTitle}>
                    Need to cancel this session?
                  </Typography>
                  <Typography className={classes.cancelCaption}>
                    Your coach will be notified when you cancel. If you need a
                    refund, message them to discuss next steps.
                  </Typography>
                </Box>
                <CancelBookingDialog
                  trigger={
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.cancelButton}
                      startIcon={
                        <CancelOutlinedIcon
                          className={classes.cancelButtonIcon}
                        />
                      }
                    >
                      Cancel Session
                    </Button>
                  }
                  confirmationCode={cancellationCode}
                  onSuccess={() => {
                    refetchLessons()
                    setOpenLesson({})
                  }}
                />
              </Box>
            ) : (
              <Box className={classes.infoBanner}>
                <InformationActionBanner
                  isMobile={isMobile}
                  isTablet={isTablet}
                  classes={classes}
                  coach={coach}
                  icon={
                    <EmojiObjectsOutlinedIcon style={{ color: '#043362' }} />
                  }
                  infoTextContent="Need to reschedule? To cancel this session, you will need to message your coach"
                />
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>
    </>
  )
}

const SessionDetailModal = ({ openLesson, setOpenLesson, refetchLessons }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const classes = useStyles()

  return (
    <Dialog
      open={!isEmpty(openLesson)}
      onClose={() => setOpenLesson({})}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      classes={isMobile ? undefined : { paper: classes.dialogPaper }}
    >
      {!isEmpty(openLesson) ? (
        <SessionDetailContent
          openLesson={openLesson}
          setOpenLesson={setOpenLesson}
          refetchLessons={refetchLessons}
        />
      ) : null}
    </Dialog>
  )
}

export default SessionDetailModal

SessionDetailModal.propTypes = {
  openLesson: PropTypes.object,
  setOpenLesson: PropTypes.func,
  refetchLessons: PropTypes.func,
}
